import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { Cell, MinistryDetail } from '../../shared/api/types';
import { Button, Card } from '../../shared/ui/components';
import { useMe } from '../session/SessionProvider';
import { SearchSelect } from '../../shared/ui/SearchSelect';

type Run = (action: () => Promise<unknown>, success: string) => Promise<void>;

/** Células del grupo: el líder o co-líder las crea, nombra, llena y elige a sus representantes (pueden ser varios). */
export function CellsCard({ ministry: m, churchId, run, busy, editing = true }: { ministry: MinistryDetail; churchId: string; run: Run; busy: boolean; editing?: boolean }) {
  const [name, setName] = useState('');
  const cells = m.cells ?? [];
  const manage = m.canManageMembers && editing;

  function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    void run(() => api.post(`/organization/churches/${churchId}/ministries/${m.id}/cells`, { name: clean }), `Célula ${clean} creada.`).then(() => setName(''));
  }

  return (
    <section className="ministry-section" aria-labelledby="cells-title">
      <h2 id="cells-title">Células ({cells.length})</h2>
      {manage ? <p className="small muted">Grupos pequeños dentro de {m.name}. Cada integrante está en una sola célula, y cada célula tiene sus representantes y su chat.</p> : null}
      {cells.length === 0 ? <p className="muted">{manage ? 'Todavía no hay células. Crea la primera abajo.' : m.canManageMembers ? 'Todavía no hay células. Toca «Editar» para crear la primera.' : 'Todavía no hay células.'}</p> : null}
      <div className="cells">
        {cells.map((c) => (
          <CellCard key={c.id} cell={c} ministry={m} churchId={churchId} run={run} busy={busy} manage={manage} />
        ))}
      </div>
      {manage && m.withoutCell.length > 0 && cells.length > 0 ? (
        <p className="small muted">
          Sin célula: {m.withoutCell.map((p) => p.name).join(', ')}.
        </p>
      ) : null}
      {manage ? (
        <form className="inline-form" onSubmit={create}>
          <label className="visually-hidden" htmlFor="new-cell">
            Nombre de la nueva célula
          </label>
          <input id="new-cell" placeholder="Nombre de la nueva célula…" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" busy={busy} disabled={!name.trim()}>
            Crear célula
          </Button>
        </form>
      ) : null}
    </section>
  );
}

function CellCard({ cell: c, ministry: m, churchId, run, busy, manage }: { cell: Cell; ministry: MinistryDetail; churchId: string; run: Run; busy: boolean; manage: boolean }) {
  const me = useMe();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(c.name);
  // Un solo botón «⋯» por persona: abre «Hacer representante» y «Quitar de la célula».
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const base = `/organization/churches/${churchId}/cells/${c.id}`;
  const cellOf = new Map((m.cells ?? []).flatMap((x) => x.members.map((p) => [p.personId, x.name] as const)));
  const addable = m.members.filter((p) => !c.members.some((x) => x.personId === p.personId));
  const canChat = m.canManageMembers || c.members.some((p) => p.personId === me.personId);
  const reps = c.representatives ?? (c.representative ? [c.representative] : []);
  const repIds = reps.map((r) => r.personId);
  const toggleRep = (p: { personId: string; name: string }) => {
    const isRep = repIds.includes(p.personId);
    const next = isRep ? repIds.filter((id) => id !== p.personId) : [...repIds, p.personId];
    void run(() => api.patch(base, { representativeIds: next }), isRep ? `${p.name} ya no es representante.` : `${p.name} ahora es representante.`);
  };

  return (
    <Card
      title={`Célula ${c.name}`}
      action={
        manage && !renaming ? (
          <Button variant="ghost" onClick={() => setRenaming(true)} aria-label={`Cambiar nombre de ${c.name}`}>
            Renombrar
          </Button>
        ) : null
      }
    >
      {renaming ? (
        <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(() => api.patch(base, { name: name.trim() }), 'Nombre actualizado.').then(() => setRenaming(false));
            }}
          >
            <label className="visually-hidden" htmlFor={`rename-${c.id}`}>
              Nombre de la célula
            </label>
            <input id={`rename-${c.id}`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            <Button type="submit" busy={busy} disabled={!name.trim()}>
              Guardar
            </Button>
          </form>
      ) : null}
      <p className="small">
        {reps.length > 1 ? 'Representantes' : 'Representante'}:{' '}
        {reps.length ? <strong>{reps.map((r) => r.name).join(', ')}</strong> : <span className="muted">sin elegir</span>}
      </p>
      <ul className="member-list">
        {c.members.length === 0 ? <li className="muted small">Sin integrantes.</li> : null}
        {c.members.map((p) => (
          <li key={p.personId} className="family-member">
            <Link to={`/app/personas/${p.personId}`} className="family-member__name">
              {p.name}
            </Link>
            {repIds.includes(p.personId) ? <span className="badge badge--active">Representante</span> : null}
            {manage ? (
              <Button variant="ghost" aria-label={`Opciones de ${p.name}`} aria-expanded={menuFor === p.personId} onClick={() => setMenuFor(menuFor === p.personId ? null : p.personId)}>
                ⋯
              </Button>
            ) : null}
            {manage && menuFor === p.personId ? (
              <span className="actions actions--wrap" role="group" aria-label={`Opciones de ${p.name}`}>
                <Button variant="secondary" disabled={busy} onClick={() => { setMenuFor(null); toggleRep(p); }}>
                  {repIds.includes(p.personId) ? 'Quitar representante' : 'Hacer representante'}
                </Button>
                <Button variant="secondary" disabled={busy} aria-label={`Quitar a ${p.name} de ${c.name}`} onClick={() => {
                    if (!window.confirm(`¿Quitar a ${p.name} de la célula ${c.name}?`)) return;
                    setMenuFor(null);
                    void run(() => api.delete(`${base}/members/${p.personId}`), `${p.name} salió de la célula.`);
                  }}>
                  Quitar de la célula
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {manage && addable.length > 0 ? (
        <form
          className="family-add"
          onSubmit={(e) => {
            e.preventDefault();
            const personId = String(new FormData(e.currentTarget).get('personId') ?? '');
            if (personId) void run(() => api.put(`${base}/members/${personId}`, {}), 'Integrante agregado a la célula.');
          }}
        >
          <div className="field">
            <label htmlFor={`add-${c.id}`}>Agregar a {c.name}</label>
            <SearchSelect
              id={`add-${c.id}`}
              name="personId"
              options={addable.map((p) => ({ value: p.personId, label: p.name, ...(cellOf.has(p.personId) ? { hint: `ahora en ${cellOf.get(p.personId)}` } : {}) }))}
            />
          </div>
          <Button type="submit" busy={busy}>
            Agregar
          </Button>
        </form>
      ) : null}
      <div className="actions">
        {canChat ? (
          <Link className="btn btn--secondary" to={`/app/chats/${c.id}`}>
            Chat de la célula
          </Link>
        ) : null}
        {manage ? (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`¿Borrar la célula ${c.name}? Sus integrantes quedan sin célula.`)) void run(() => api.delete(base), `Célula ${c.name} borrada.`);
            }}
          >
            Borrar célula
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
